import { Foundation } from 'src/.generated/Foundation.EnterpriseWeb.model';
import ARProductModal from './ARProductModal';
import ThreekitVisualizer from 'components/product/ThreekitVisualizer/ThreekitVisualizer';
import ModalWrapper from '../ModalWrapper/ModalWrapper';
import { useCallback, useEffect, useState } from 'react';
import Headline from '../Headline/Headline';
import { getBreakpoint, useCurrentScreenType } from 'lib/utils/get-screen-type';
import ModalPortal from 'src/helpers/Modals/ModalPortal';

let modalInPlace: string | null = null;

export type ProductMediaModalProps =
  Foundation.EnterpriseWeb.RenewalByAndersen.FieldSets.Cards.ProductMediaAR & {
    modalId: string;
    headlineText: string;
    isModalOpen: boolean;
    setIsModalOpen: () => void;
  };

const ProductMediaModal = ({
  modalId,
  headlineText,
  isModalOpen,
  setIsModalOpen,
  ...props
}: ProductMediaModalProps) => {
  const [angle, setAngle] = useState(0);
  const [showVisModal, setShowVisModal] = useState(false);
  const isCard = props.fields?.productModal?.templateName?.includes('Card') ? true : false;
  const { currentScreenWidth } = useCurrentScreenType();
  const isDesktop = currentScreenWidth >= getBreakpoint('lg');

  const handleOrientationChange = useCallback(() => {
    if (window.screen?.orientation) {
      setAngle(window.screen.orientation.angle);
    }
  }, []);

  useEffect(() => {
    if (modalInPlace === null && !isCard) {
      modalInPlace = headlineText;
      setShowVisModal(true);
    } else if (modalInPlace === headlineText) {
      setShowVisModal(true);
    } else {
      setShowVisModal(false);
    }

    return () => {
      if (modalInPlace === headlineText) {
        modalInPlace = null;
      }
    };
  }, [headlineText, isCard]);

  useEffect(() => {
    let orientation: ScreenOrientation | undefined;

    if (screen?.orientation) {
      orientation = screen.orientation;
      setAngle(orientation.angle);
      orientation.addEventListener('change', handleOrientationChange);
    }

    return () => {
      if (orientation) {
        orientation.removeEventListener('change', handleOrientationChange);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalId]);

  const size = angle === 90 ? 'fluid' : isDesktop ? 'fluid' : 'extra-large';

  return (
    <>
      <ModalPortal>
        {isCard && isModalOpen ? (
          <ARProductModal
            modalId={modalId}
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            {...props.fields?.productModal}
          />
        ) : (
          showVisModal && (
            <ModalWrapper
              isModalOpen={isModalOpen}
              size={size}
              handleClose={setIsModalOpen}
              modalLabel={modalId}
              customOverlayclass="flex"
            >
              <div id="visHeadline" className="lg:w-[75vw]">
                <Headline
                  classes={
                    'text-dark-gray md:text-black font-serif text-sm-xs md:text-s font-heavy md:font-bold mb-s md:mb-m ml-m'
                  }
                  fields={{
                    headlineText: {
                      value: headlineText,
                    },
                  }}
                />
                <ThreekitVisualizer
                  {...({
                    fields: props.fields?.productModal?.fields,
                    rendering: {
                      dataSource: 'manually-passed', // Need to specify datasource to render
                      componentName: 'ThreekitVisualizer',
                    },
                  } as any)}
                />
              </div>
            </ModalWrapper>
          )
        )}
      </ModalPortal>
    </>
  );
};

export default ProductMediaModal;
